<script lang="ts" setup>
  import { useI18n } from "vue-i18n";
  import { useAppStore } from "@/stores/app";
  import { solveFilter } from "@/utils/filterSolver";
  import { computed, onMounted, ref } from 'vue';
  import { useRewardsStore } from "@/stores/rewards";
  import { useCategoryStore } from "@/stores/categories";
  import instagramLogo from '@/assets/images/instagram-logo.svg';
  import twitterLogo from '@/assets/images/twitter-X.svg';
  import facebookLogo from '@/assets/images/facebook-logo.svg';
  import youtubeLogo from '@/assets/images/youtube-logo.svg';
  import linkedInLogo from '@/assets/images/linkedin-logo.svg';
  import * as amplitude from "@amplitude/analytics-browser";
  import { useSessionStore } from '@/stores/session';
  import { useCardsStore } from '@/stores/cards';

  const { t } = useI18n({ useScope: 'global' });
  const appStore = useAppStore();
  const rewardsStore = useRewardsStore();
  const categoryStore = useCategoryStore();
  const sessionStore = useSessionStore();
  const cardsStore = useCardsStore();

  const filter = solveFilter('--v-theme-neutral-600');
  const filterHovered = solveFilter('--v-theme-primary');

  const socials = {
    'instagram': instagramLogo,
    'twitter': twitterLogo,
    'facebook': facebookLogo,
    'youtube': youtubeLogo,
    'linkedin': linkedInLogo,
  };

  const supportRoutes = {
    '/support/privacy': t('support.privacy'),
    '/support/terms': t('support.terms'),
  };

  const categoryRoutes = ref<{ [ key: string ]: string; }>({});

  const offersHidden = computed(() => {
    if(!cardsStore.initialized) return true;

    return appStore.isAmexUxLimited && cardsStore.hasOnlyAmexCards;
  });

  onMounted(async () => {
    await categoryStore.fetchCategories();

    const categories = categoryStore.categories;
    for (const category of categories) {
      if (category?.platformName) {
        if (Object.values(categoryRoutes.value).length >= 8) { // Limit to 8 categories in the footer
          break;
        }
        categoryRoutes.value[ '/rewards?category=' + category?.platformName ] = category.name;
      }
    }
  });

  function isNotEmptyLink(link: string): boolean {
    return !(link === '' || link === undefined || link === null || link === '#');
  }

  function selectPopularCategory(category: string) {
    rewardsStore.setSearchCategory(category);
    amplitude.track('Offer Category Clicked', {
      'In-App Location': 'Footer',
      'Category Name': category,
    });
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleReferralClick() {
    appStore.showReferralRockModal = true;

    amplitude.track('Invite Friends Clicked', {
      'In-App Location': 'Navigation Footer',
    });
  }
</script>

<template>
  <v-footer class="footer-brand">
    <v-container>
      <v-row dense class="footer-row">
        <v-col xl="3" lg="3" md="6" sm="12" cols="12">
          <div class="w-100">
            <div class="t-subtitle-1 t-bold footer-title">
              {{ $theme.getLabel('footer_title').value }}
            </div>
            <div class="t-body-2 t-medium footer-description">
              {{ $theme.getLabel('footer_description').value }}
            </div>
            <div class="social-icons">
              <template v-for="(socialLogo, socialName) in socials">
                <a
                  :key="`social-icon-${socialName}`"
                  v-if="isNotEmptyLink($theme.getAnchor(`footer_social_${socialName}`).href)"
                  :href="$theme.getAnchor(`footer_social_${socialName}`).href"
                  target="_blank"
                >
                  <img
                    :src="socialLogo"
                    :alt="socialName"
                  />
                </a>
              </template>
            </div>
          </div>
        </v-col>
        <v-col xl="2" lg="2" md="2" sm="12" cols="12">
          <!-- Desktop -->
          <div class="d-md-block d-sm-none d-none">
            <div class="t-body-2 t-bold">
              {{
                appStore.isSubscriptionProgram || appStore.isWhiteLabel ? appStore.client!.name : $t('footer.changeit')
              }}
            </div>
            <div v-if="!offersHidden" class="t-body-2 t-medium">
              <router-link to="/rewards" @click.native="scrollToTop">
                {{ $t('nav.all-deals') }}
              </router-link>
            </div>
            <div v-if="appStore.isReferralEnabled" class="t-body-2 t-medium">
              <router-link to="" @click="handleReferralClick" @click.native="scrollToTop">
                {{ $t('nav.refer') }}
              </router-link>
            </div>
            <div v-if="!offersHidden" class="t-body-2 t-medium">
              <router-link to="/activity" @click.native="scrollToTop">
                {{ t('nav.my-card') }}
              </router-link>
            </div>
            <div class="t-body-2 t-medium">
              <router-link to="/account" @click.native="scrollToTop">
                {{ $t('nav.my-account') }}
              </router-link>
            </div>
          </div>
          <!-- Mobile -->
          <v-expansion-panels variant="accordion" class="d-md-none d-sm-block">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="t-body-2 t-bold mb-0">
                  {{
                    appStore.isSubscriptionProgram || appStore.isWhiteLabel ? appStore.client!.name : $t('footer.changeit')
                  }}
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div v-if="!offersHidden" class="t-body-2 t-medium">
                  <router-link to="/rewards" @click.native="scrollToTop">
                    {{ $t('nav.all-deals') }}
                  </router-link>
                </div>
                <div v-if="appStore.isReferralEnabled" class="t-body-2 t-medium">
                  <router-link to="" @click="handleReferralClick">
                    {{ $t('nav.refer') }}
                  </router-link>
                </div>
                <div v-if="!offersHidden" class="t-body-2 t-medium">
                  <router-link to="/activity" @click.native="scrollToTop">
                    {{ t('nav.my-card') }}
                  </router-link>
                </div>
                <div class="t-body-2 t-medium">
                  <router-link to="/account" @click.native="scrollToTop">
                    {{ $t('nav.my-account') }}
                  </router-link>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
        <v-col v-if="!offersHidden" xl="3" lg="3" sm="12" cols="12" class="d-lg-flex d-md-none d-flex">
          <!-- Desktop -->
          <div class="d-md-block d-sm-none d-none">
            <v-row>
              <v-col cols="12" class="pb-0 pl-0 pr-0 justify-start">
                <div class="t-body-2 t-bold">
                  {{ $t('category.popular') }}
                </div>
              </v-col>
              <v-col cols="6" class="col-link justify-start" v-for="(label, routePath) in categoryRoutes">
                <div class="category-route">
                  <router-link
                    :to="routePath.toString()"
                    @click="selectPopularCategory(label)"
                    @click.native="scrollToTop"
                  >
                    {{ label }}
                  </router-link>
                </div>
              </v-col>
            </v-row>
          </div>
          <!-- Mobile -->
          <v-expansion-panels variant="accordion" class="d-md-none d-sm-block">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="t-body-2 t-bold mb-0">
                  {{ $t('category.popular') }}
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="category-route" v-for="(label, routePath) in categoryRoutes">
                  <router-link :to="routePath.toString()" @click="selectPopularCategory(label)"
                               @click.native="scrollToTop">
                    {{ label }}
                  </router-link>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
        <v-col xl="2" lg="2" md="2" sm="12" cols="12">
          <!-- Desktop -->
          <div class="d-md-block d-sm-none d-none">
            <div class="t-body-2 t-bold">
              {{ $t('support') }}
            </div>
            <div class="t-body-2 t-medium" v-if="isNotEmptyLink($theme.getAnchor('footer_contact_us').href)">
              <a :href="$theme.getAnchor('footer_contact_us').href" target="_blank">
                {{ $theme.getAnchor('footer_contact_us').value }}
              </a>
            </div>
            <div class="t-body-2 t-medium" v-if="isNotEmptyLink($theme.getAnchor('footer_faq').href)">
              <a :href="$theme.getAnchor('footer_faq').href" target="_blank">
                {{ $theme.getAnchor('footer_faq').value }}
              </a>
            </div>
            <div class="t-body-2 t-medium" v-for="(label, route) in supportRoutes">
              <router-link :to="route" target="_blank">
                {{ label }}
              </router-link>
            </div>
          </div>
          <!-- Mobile -->
          <v-expansion-panels variant="accordion" class="d-md-none d-sm-block">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="t-body-2 t-bold mb-0">
                  {{ $t('support') }}
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="t-body-2 t-medium" v-if="isNotEmptyLink($theme.getAnchor('footer_contact_us').href)">
                  <a :href="$theme.getAnchor('footer_contact_us').href" target="_blank">
                    {{ $theme.getAnchor('footer_contact_us').value }}
                  </a>
                </div>
                <div class="t-body-2 t-medium" v-if="isNotEmptyLink($theme.getAnchor('footer_faq').href)">
                  <a :href="$theme.getAnchor('footer_faq').href" target="_blank">
                    {{ $theme.getAnchor('footer_faq').value }}
                  </a>
                </div>
                <div class="t-body-2 t-medium" v-for="(label, route) in supportRoutes">
                  <router-link :to="route" target="_blank">
                    {{ label }}
                  </router-link>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
        <v-col xl="2" lg="2" md="2" sm="12" cols="12">
          <!-- Desktop -->
          <div class="d-md-block d-sm-none d-none">
            <div class="t-body-2 t-bold">
              {{ $theme.getLabel('footer_about_subTitle').value }}
            </div>
            <div class="t-body-2 t-medium">
              <a :href="$theme.getAnchor('footer_about_1').href" target="_blank">
                {{ $theme.getAnchor('footer_about_1').value }}
              </a>
            </div>
            <div class="t-body-2 t-medium">
              <a :href="$theme.getAnchor('footer_about_2').href" target="_blank">
                {{ $theme.getAnchor('footer_about_2').value }}
              </a>
            </div>
          </div>
          <!-- Mobile -->
          <v-expansion-panels variant="accordion" class="d-md-none d-sm-block">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="t-body-2 t-bold mb-0">
                  {{ $theme.getLabel('footer_about_subTitle').value }}
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="t-body-2 t-medium">
                  {{ $t('footer.mission') }}
                </div>
                <div class="t-body-2 t-medium">
                  {{ $t('footer.history') }}
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-footer>
  <v-footer class="footer-ascend" v-if="!appStore.isWhiteLabel">
    <v-container>
      <div class="made-with">
        <span>
          {{ t('footer.madewith') }}
        </span>
        <v-icon icon="favorite" class="ml-2 mr-2 love"/>
        <span>
          {{ t('footer.by') }}
        </span>
        <span class="ascend-logo">
          <a href="https://www.ascendfs.com" target="_blank">
            <img src="@/assets/images/ascend-logo.svg" alt="Ascend Logo"/>
          </a>
        </span>
      </div>

      <div class="change-it-logo">
        <a href="https://www.ascendfs.com/programs/change-it" target="_blank">
          <img src="@/assets/images/change-it-logo.svg" alt="Change It Logo"/>
        </a>
      </div>
    </v-container>
  </v-footer>
</template>

<style lang="scss" scoped>
  footer.footer-brand {
    background-color: rgb(var(--v-theme-neutral-100));
    color: rgb(var(--v-theme-neutral600));
    padding: 46px 0;

    .category-route {
      font-size: getFontSize('body-2');
      font-weight: getFontWeight(medium);
      text-transform: capitalize;

      a {
        font-size: getFontSize('body-2');
        font-weight: getFontWeight(medium);
        text-transform: capitalize;
      }
    }

    .social-icons {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px;
      margin-top: 32px;
      gap: 42px;


      a {
        img {
          width: 32px;
          filter: v-bind(filter);
          transition: 0.5s ease all;
        }
      }

      a:hover {
        img {
          filter: v-bind(filterHovered);
        }
      }
    }

    .footer-row {
      & > div {
        padding: 0 1rem;
      }
    }

    // Expansion Panels / Accordion
    .v-expansion-panels.v-expansion-panels--variant-accordion {
      .v-expansion-panel {
        background: transparent;

        :deep(.v-expansion-panel-title), :deep(.v-expansion-panel-title.v-expansion-panel-title--active) {

          border-bottom: 1px solid rgba(var(--v-theme-on-background), 0.1);

          .v-expansion-panel-title__overlay {
            background-color: transparent;
          }
        }

        :deep(.v-expansion-panel-text) {
          opacity: 1;
        }

        :deep(.v-expansion-panel__shadow) {
          display: none;
        }
      }
    }

    // Phones
    @media(min-width: breakpoint(sm) - 1px) {
      .t-subtitle-1.t-bold, .t-body-2.t-bold {
        margin-bottom: 30px;
      }

      .t-body-2.t-medium {
        margin-bottom: 16px;
      }

      .col-link {
        padding: 0 0 16px;
      }
    }

    // Tablets and down
    @media(max-width: breakpoint(md) - 1px) {
      .t-body-2 {
        margin-top: 10px;
        margin-bottom: 10px;
      }

      .footer-description {
        margin-bottom: 26px;
      }

      :deep(.v-label.v-field-label), :deep(.v-field__field) {
        font-size: getFontSize(body-2);
      }

      .social-icons {
        justify-content: center;
      }

      .category-route {
        margin-top: 10px;
        margin-bottom: 16px;
      }
    }

    // Wide desktops and up
    .v-container {
      padding-right: 30px;
    }
  }

  footer.footer-ascend {

    padding: 0;

    .v-container {
      position: relative;
    }

    .made-with {
      display: flex;
      align-items: center;
      font-size: getFontSize(body-2);
      color: rgb(var(--v-theme-neutral-400));
    }

    .love {
      color: rgb(var(--v-theme-status-red-darken-2));
    }

    .ascend-logo {
      margin-left: 10px;

      a {
        display: flex;
        align-items: center;

        img {
          width: 70px;
        }
      }
    }

    .change-it-logo {
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);

      img {
        width: 120px;
      }
    }

    @media(max-width: breakpoint(md) - 1px) {
      .made-with {
        text-align: center;
        justify-content: center;
      }

      .change-it-logo {
        display: none;
      }
    }
  }

  @media(max-width: breakpoint(md) - 1px) {
    footer.footer-ascend {
      padding-bottom: 60px;
    }
  }
</style>
